import { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "./ui";
import { BrandMark } from "@/components/Brand";
import { errMsg } from "@/lib/api";

const TYPES = [["cnpj", "CNPJ"], ["cpf", "CPF"], ["telefone", "Telefone"], ["email", "E-mail"], ["aleatoria", "Chave aleatória"]];
const EMPTY = { pix_key: "", pix_key_type: "cnpj", beneficiary_name: "", beneficiary_city: "", pix_code_override: "", instructions: "" };

const Logo = ({ s, reload }) => {
  const { api } = useOutletContext();
  const ref = useRef(null);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post("/admin/settings/logo", fd); toast.success("Logomarca atualizada."); reload(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <section className="space-y-3">
      <h2 className="font-display text-lg font-semibold">Logomarca oficial</h2>
      <div className="flex items-center gap-4 rounded-lg border border-zap-line bg-white p-4">
        <div className="rounded-md bg-zap-deep p-3"><BrandMark hasLogo={s.has_logo} version={s.logo_version} className="h-16" testId="settings-logo" /></div>
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">{s.has_logo ? "Logomarca enviada pelo painel e exibida no cabeçalho." : "Logomarca oficial da igreja em uso. Envie outro PNG apenas se a igreja atualizar a marca."}</p>
          <input ref={ref} type="file" accept="image/png" className="hidden" data-testid="logo-file-input" onChange={upload} />
          <Button data-testid="logo-upload" variant="outline" onClick={() => ref.current.click()}><Upload className="mr-2 h-4 w-4" /> Enviar PNG</Button>
        </div>
      </div>
    </section>
  );
};

export default function PixSettings() {
  const { api } = useOutletContext();
  const [s, setS] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const reload = () => api.get("/admin/settings").then((r) => { setS(r.data); setF({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, r.data[k] || EMPTY[k]])) }); });
  useEffect(() => { reload(); }, [api]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.put("/admin/settings/pix", f); toast.success("Pix salvo. Já está disponível para os participantes."); reload(); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  if (!s) return null;
  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Pix e marca" description="Dados exibidos nos botões PIX, CONTRIBUIR e COPIAR PIX." />
      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto">
        <div className="grid max-w-5xl gap-10 px-5 py-6 md:px-8 lg:grid-cols-[1fr_300px]">
          <form onSubmit={save} className="space-y-4">
            <h2 className="font-display text-lg font-semibold">Chave Pix da igreja</h2>
            <div className="grid gap-3 sm:grid-cols-[170px_1fr]">
              <div className="space-y-1.5"><Label>Tipo</Label>
                <Select value={f.pix_key_type} onValueChange={set("pix_key_type")}>
                  <SelectTrigger data-testid="pix-key-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Chave</Label><Input data-testid="pix-key-input" value={f.pix_key} onChange={set("pix_key")} /></div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Nome do favorecido</Label><Input data-testid="pix-name-input" value={f.beneficiary_name} onChange={set("beneficiary_name")} maxLength={60} /></div>
              <div className="space-y-1.5"><Label>Cidade do favorecido *</Label><Input data-testid="pix-city-input" value={f.beneficiary_city} onChange={set("beneficiary_city")} maxLength={40} placeholder="Ex.: São Paulo" />
                <p className="text-xs text-muted-foreground">Obrigatória: sem ela o banco não reconhece o QR Code.</p></div>
            </div>
            <div className="space-y-1.5">
              <Label>Código “Pix Copia e Cola” do banco (opcional)</Label>
              <Textarea data-testid="pix-override-input" value={f.pix_code_override} onChange={set("pix_code_override")} rows={3} className="font-mono text-xs" />
              <p className="text-xs text-muted-foreground">Só use se tiver o código longo do app do banco (começa com 000201). <b>Não coloque a chave aqui.</b> Se não tiver, deixe em branco: o QR Code é gerado pela chave, nome e cidade acima.</p>
              {f.pix_code_override.trim() && !f.pix_code_override.trim().startsWith("000201") && (
                <p data-testid="pix-override-warning" className="text-sm font-semibold text-red-700">Isto não é um código Pix Copia e Cola. Apague este campo para o QR Code funcionar.</p>
              )}
            </div>
            <div className="space-y-1.5"><Label>Orientação aos participantes (opcional)</Label><Textarea data-testid="pix-instructions-input" value={f.instructions} onChange={set("instructions")} rows={3} /></div>
            <Button data-testid="pix-save" disabled={busy}>Salvar Pix</Button>
          </form>
          <div className="space-y-6">
            <section className="space-y-3">
              <h2 className="font-display text-lg font-semibold">Prévia</h2>
              {s.configured ? (
                <div className="space-y-2 rounded-lg border border-zap-line bg-white p-4">
                  <div className="flex justify-center"><QRCodeSVG data-testid="pix-preview-qr" value={s.pix_code} size={180} /></div>
                  <p data-testid="pix-preview-code" className="break-all font-mono text-[11px] text-muted-foreground">{s.pix_code}</p>
                  <p className="text-xs text-muted-foreground">Faça um teste com o aplicativo do banco antes de divulgar.</p>
                </div>
              ) : <p data-testid="pix-not-configured" className="rounded-lg border border-dashed border-red-300 bg-red-50 p-4 text-sm text-red-800">QR Code Pix ainda não está pronto. Preencha a chave, o nome e a <b>cidade</b> do favorecido e salve. Os participantes verão um aviso ao tocar nos botões.</p>}
            </section>
            <Logo s={s} reload={reload} />
          </div>
        </div>
      </div>
    </div>
  );
}
